import { useEffect, useState } from "react";
import styled from "styled-components";
import Cookies from "universal-cookie";
import * as XLSX from "xlsx";
import { getTranslate, getValue } from "../contexts/Utils";
import Api from "../contexts/Api";

const formatEuro = (n) => {
  if (n == null || n === "") return "—";
  const num = typeof n === "number" ? n : parseFloat(String(n).replace(",", "."));
  if (Number.isNaN(num)) return "—";
  return `${num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

const formatEmissionDate = (raw) => {
  if (!raw) return "—";
  const s = typeof raw === "string" ? raw : String(raw);
  const d = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  }
  return s;
};

const Transactions = ({ lang }) => {
  const cookies = new Cookies();
  const user = cookies.get('nudge_user');
  const isSuperAdmin = Number(user?.role) === 3;
  const [transactions, setTransactions] = useState([]);
  const [subscriptionInvoices, setSubscriptionsInvoices] = useState([]);
  const [adminTab, setAdminTab] = useState('mine');
  const [adminAllRows, setAdminAllRows] = useState([]);
  const [adminAllLoading, setAdminAllLoading] = useState(false);

  const statusColors = {
    1: {
      color: 'black',
      background: '#e3e3e3',
    },
    2: {
      color: 'white',
      background: '#ba902b',
    },
    3: {
      color: 'white',
      background: '#e36060',
    },
    4: {
      color: 'white',
      background: '#38a438',
    },
    5: {
      color: 'white',
      background: '#e36060',
    },
  }

  useEffect(() => {
    const getTransactions = async () => {
      // Super-admin : liste globale (GET sans filtre expert/industriel), comme avant l’ajout du second onglet.
      if (isSuperAdmin) {
        const query = {
          fields: [
            'transaction_id',
            'transactions.mission_id',
            'missions.title',
            'transactions.status'
          ],
        };
        const params = {
          query,
          endpoint: "/transactions",
          method: 'GET'
        };
        const res = await Api(params);
        if (res.success && Array.isArray(res.data)) {
          setTransactions(res.data);
        }
        return;
      }

      let query = {
        fields: [
          'transaction_id',
          'transactions.mission_id',
          'missions.title',
          'transactions.status'
        ],
      }

      if (user.role === 1) query.industrial_id = user.user_id;
      else if (user.role === 2) query.expert_id = user.user_id;

      const params = {
        query,
        endpoint: "/transactions",
        method: 'GET'
      }

      const transactionsQuery = await Api(params);
      if (transactionsQuery.success) {
        setTransactions(transactionsQuery.data);
      }
    }

    getTransactions();
  }, [user.role, user.user_id, isSuperAdmin])

  useEffect(() => {
    const getSubscriptionInvoices = async () => {

      const params = {
        endpoint: "/stripe/invoices/subscription",
        method: 'GET'
      }

      const subscription = await Api(params);
      if (subscription.success) {
        setSubscriptionsInvoices(subscription.data);
      }
    }

    user.role === 1 && getSubscriptionInvoices();
  }, [user.role, user.user_id])

  useEffect(() => {
    if (!isSuperAdmin || adminTab !== 'all') return;
    const load = async () => {
      setAdminAllLoading(true);
      try {
        const params = {
          query: { admin_all: 1 },
          endpoint: "/transactions",
          method: 'GET',
        };
        const res = await Api(params);
        if (res.success && Array.isArray(res.data)) {
          setAdminAllRows(res.data);
        }
      } finally {
        setAdminAllLoading(false);
      }
    };
    load();
  }, [isSuperAdmin, adminTab]);

  const exportAllInvoicesExcel = () => {
    if (!adminAllRows.length) return;
    const header = [
      'N° de facture',
      'Date d\'émission',
      'Nom de l\'expert',
      'Prénom de l\'expert',
      'Titre de la mission',
      'Client final',
      'Montant HT (€)',
      'Montant TVA (€)',
      'Montant net (€)',
    ];
    const data = adminAllRows.map((r) => [
      r.invoice_id ?? '',
      formatEmissionDate(r.emission_date),
      r.expert_name ?? '',
      r.expert_firstname ?? '',
      r.mission_title ?? '',
      (r.final_client_label && String(r.final_client_label).trim()) ? r.final_client_label : '',
      r.price_ht != null && r.price_ht !== '' ? Number(r.price_ht) : '',
      r.amount_tva != null && r.amount_tva !== '' ? Number(r.amount_tva) : '',
      r.amount_net != null && r.amount_net !== '' ? Number(r.amount_net) : '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Factures');
    XLSX.writeFile(wb, `factures-tous-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleDownload = async (transaction_id, invoice_id) => {
    const params = {
      query: { transaction_id, role: user.role, invoice_id },
      endpoint: "/invoice",
      method: 'GET'
    }

    const downloadQuery = await Api(params);

    // download file
    if (downloadQuery.success) {
      const link = document.createElement('a');
      link.href = "data:application/pdf;base64," + downloadQuery.buffer;
      link.download = `${invoice_id}.pdf`;
      link.click();
    }
  }

  return <S.Container>
    <h1>{getTranslate('my_bills', lang)}</h1>
    {isSuperAdmin && (
      <S.TabBar role="tablist" aria-label="Factures">
        <S.Tab
          type="button"
          role="tab"
          aria-selected={adminTab === 'mine'}
          $active={adminTab === 'mine'}
          onClick={() => setAdminTab('mine')}
        >
          Mes factures
        </S.Tab>
        <S.Tab
          type="button"
          role="tab"
          aria-selected={adminTab === 'all'}
          $active={adminTab === 'all'}
          onClick={() => setAdminTab('all')}
        >
          Toutes les factures
        </S.Tab>
      </S.TabBar>
    )}
    {isSuperAdmin && adminTab === 'all' ? <>
      <S.AllPanel>
        <S.AllHeader>
          <S.AllHeaderLeft>
            <S.AllHeaderTitle>Vue détaillée</S.AllHeaderTitle>
            {!adminAllLoading && (
              <S.AllCountBadge aria-live="polite">
                {adminAllRows.length} facture{adminAllRows.length !== 1 ? 's' : ''}
              </S.AllCountBadge>
            )}
          </S.AllHeaderLeft>
          <S.ExportBtn
            type="button"
            onClick={exportAllInvoicesExcel}
            disabled={adminAllLoading || !adminAllRows.length}
          >
            Exporter Excel
          </S.ExportBtn>
        </S.AllHeader>
        <S.TableWrap>
          <S.Table>
            <thead>
              <tr>
                <th>N° de facture</th>
                <th>Date d&apos;émission</th>
                <th>Nom de l&apos;expert</th>
                <th>Prénom de l&apos;expert</th>
                <th>Titre de la mission</th>
                <th>Client final</th>
                <th className="amount">Montant HT</th>
                <th className="amount">TVA</th>
                <th className="amount">Net TTC</th>
                <th className="action">PDF</th>
              </tr>
            </thead>
            <tbody>
              {adminAllLoading ? (
                <tr>
                  <S.LoadingCell colSpan={10}>
                    <S.LoadingText>Chargement…</S.LoadingText>
                  </S.LoadingCell>
                </tr>
              ) : adminAllRows.length ? adminAllRows.map((row) => (
                <tr key={`${row.transaction_id}-${row.invoice_id}`}>
                  <td><S.InvoicePill>{row.invoice_id}</S.InvoicePill></td>
                  <td>{formatEmissionDate(row.emission_date)}</td>
                  <td>{row.expert_name ?? '—'}</td>
                  <td>{row.expert_firstname ?? '—'}</td>
                  <td><S.MissionCell title={row.mission_title}>{row.mission_title ?? '—'}</S.MissionCell></td>
                  <td><S.ClientCell title={row.final_client_label}>{row.final_client_label?.trim() ? row.final_client_label : '—'}</S.ClientCell></td>
                  <td className="amount">{formatEuro(row.price_ht)}</td>
                  <td className="amount muted">{formatEuro(row.amount_tva)}</td>
                  <td className="amount strong">{formatEuro(row.amount_net)}</td>
                  <td className="action">
                    <S.AllDownloadBtn type="button" onClick={() => handleDownload(row.transaction_id, row.invoice_id)} aria-label={getTranslate('download', lang)}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M180.7 395.3C183.8 398.4 187.9 400 192 400s8.188-1.562 11.31-4.688l144-144c6.25-6.25 6.25-16.38 0-22.62s-16.38-6.25-22.62 0L208 345.4V48C208 39.16 200.8 32 192 32S176 39.16 176 48v297.4L59.31 228.7c-6.25-6.25-16.38-6.25-22.62 0s-6.25 16.38 0 22.62L180.7 395.3zM368 448h-352C7.156 448 0 455.2 0 464S7.156 480 16 480h352c8.844 0 16-7.156 16-16S376.8 448 368 448z" /></svg>
                    </S.AllDownloadBtn>
                  </td>
                </tr>
              )) : (
                <tr>
                  <S.EmptyCell colSpan={10}>{getTranslate('no_invoice_yet', lang)}</S.EmptyCell>
                </tr>
              )}
            </tbody>
          </S.Table>
        </S.TableWrap>
      </S.AllPanel>
    </> : <>
    <h2>Missions</h2>
    {transactions.length ? <>
      <S.Columns>
        <span className="id">{getTranslate('invoice_id', lang)}</span>
        <span className="status_column">État</span>
        <span className="title">{getTranslate('mission_title', lang)}</span>
        <span className="price">{getTranslate('price_ht', lang)}</span>
      </S.Columns>
      {transactions.map(t => <S.Card key={t.invoice_id}>
        <span className="id"><span>{t.invoice_id}</span></span>
        <span className="status" style={statusColors[t.status]}><span>{getValue(window._DATA, ['types', 'transaction_status', t.status, 'label'])}</span></span>
        <span className="title">{t.title}</span>
        <S.CardRight className="card-right">
          <span className="price">{t.price} €</span>
          <div className="download">
            <svg onClick={() => handleDownload(t.transaction_id, t.invoice_id)} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M180.7 395.3C183.8 398.4 187.9 400 192 400s8.188-1.562 11.31-4.688l144-144c6.25-6.25 6.25-16.38 0-22.62s-16.38-6.25-22.62 0L208 345.4V48C208 39.16 200.8 32 192 32S176 39.16 176 48v297.4L59.31 228.7c-6.25-6.25-16.38-6.25-22.62 0s-6.25 16.38 0 22.62L180.7 395.3zM368 448h-352C7.156 448 0 455.2 0 464S7.156 480 16 480h352c8.844 0 16-7.156 16-16S376.8 448 368 448z" /></svg>
            <span>{getTranslate('download', lang)}</span>
          </div>
        </S.CardRight>
      </S.Card>)}
    </>
      : <span>{getTranslate('no_invoice_yet', lang)}</span>}
    {user.role === 1 && <>
      <h2>Abonnement</h2>
      {subscriptionInvoices.length ? <>
        <S.Columns>
          <span className="id">{getTranslate('invoice_id', lang)}</span>
          <span className="title">Désignation de l'abonnement</span>
          <span className="price">{getTranslate('price_ht', lang)}</span>
        </S.Columns>
        {subscriptionInvoices.filter((t) => t && t.invoice_id).map((t) => (
          <S.Card key={t.invoice_id}>
            <span className="id"><span>{t.invoice_id}</span></span>
            <span className="title">{t.title}</span>
            <S.CardRight className="card-right">
              <span className="price">{t.price / 100} €</span>
              <div className="download">
                <svg onClick={() => {
                  const link = document.createElement('a');
                  link.href = t.url;
                  link.click();
                }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M180.7 395.3C183.8 398.4 187.9 400 192 400s8.188-1.562 11.31-4.688l144-144c6.25-6.25 6.25-16.38 0-22.62s-16.38-6.25-22.62 0L208 345.4V48C208 39.16 200.8 32 192 32S176 39.16 176 48v297.4L59.31 228.7c-6.25-6.25-16.38-6.25-22.62 0s-6.25 16.38 0 22.62L180.7 395.3zM368 448h-352C7.156 448 0 455.2 0 464S7.156 480 16 480h352c8.844 0 16-7.156 16-16S376.8 448 368 448z" /></svg>
                <span>{getTranslate('download', lang)}</span>
              </div>
            </S.CardRight>
          </S.Card>
        ))}
      </>
        : <span>{getTranslate('no_invoice_yet', lang)}</span>}</>}
    </>}
  </S.Container>
}

export default Transactions;

const S = {}
S.Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;

  & > h1 {
    text-transform: uppercase;
    color: ${({ theme }) => theme.text};
    font-size: 23px;
    margin: 0;
    font-weight: bold;
    margin-bottom: 15px;
  }

  & h2 {
    color: ${({ theme }) => theme.text};
  }
`

S.TabBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

S.Tab = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid ${({ theme, $active }) => $active ? theme.text : `${theme.text}40`};
  background: ${({ theme, $active }) => $active ? theme.card : 'transparent'};
  color: ${({ theme }) => theme.text};
  font-size: 14px;
  cursor: pointer;
  font-weight: ${({ $active }) => $active ? 600 : 500};

  &:hover {
    border-color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.card};
  }
`

S.AllPanel = styled.div`
  border-radius: 14px;
  overflow: hidden;
  background: ${({ theme }) => theme.card};
  box-shadow:
    0 1px 3px ${({ theme }) => theme.text}12,
    0 8px 24px ${({ theme }) => theme.text}0d;
  border: 1px solid ${({ theme }) => theme.text}18;
`

S.AllHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.text}14;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.text}06 0%,
    transparent 100%
  );
`

S.AllHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

S.AllHeaderTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.text};
`

S.AllCountBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.text}12;
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => theme.text}20;
`

S.ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(145deg, #ba902b 0%, #9a7824 100%);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: 0 2px 8px ${({ theme }) => theme.text}22;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }

  &:not(:disabled):hover {
    filter: brightness(1.08);
  }
`

S.TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
`

S.Table = styled.table`
  width: 100%;
  min-width: 1080px;
  border-collapse: collapse;
  font-size: 13px;
  color: ${({ theme }) => theme.text};

  th, td {
    padding: 12px 14px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.text}12;
    vertical-align: middle;
  }

  thead th {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.text};
    opacity: 0.72;
    background: ${({ theme }) => theme.text}06;
    white-space: nowrap;
  }

  tbody tr {
    transition: background 0.15s ease;
  }

  tbody tr:nth-child(even) td {
    background: ${({ theme }) => theme.text}04;
  }

  tbody tr:hover td {
    background: ${({ theme }) => theme.text}0c !important;
  }

  th.amount,
  td.amount {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  td.amount.muted {
    opacity: 0.85;
    font-size: 12px;
  }

  td.amount.strong {
    font-weight: 600;
  }

  th.action,
  td.action {
    text-align: center;
    width: 52px;
  }
`

S.InvoicePill = styled.span`
  display: inline-block;
  max-width: 140px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  padding: 4px 10px;
  border-radius: 6px;
  background: ${({ theme }) => theme.text}10;
  border: 1px solid ${({ theme }) => theme.text}18;
`

S.MissionCell = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 220px;
  line-height: 1.35;
`

S.ClientCell = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 180px;
  line-height: 1.35;
  opacity: 0.95;
`

S.LoadingCell = styled.td`
  padding: 48px 24px !important;
  text-align: center;
  border: none !important;
  background: transparent !important;
`

S.LoadingText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  opacity: 0.8;
  letter-spacing: 0.02em;
`

S.EmptyCell = styled.td`
  padding: 40px 24px !important;
  text-align: center;
  font-size: 14px;
  opacity: 0.8;
  border: none !important;
`

S.AllDownloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  background: ${({ theme }) => theme.text}0d;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s ease, transform 0.12s ease;

  svg {
    width: 17px;
    height: 17px;
    fill: ${({ theme }) => theme.text};
    opacity: 0.9;
  }

  &:hover {
    background: ${({ theme }) => theme.text}18;
  }

  &:active {
    transform: scale(0.96);
  }
`

S.IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;

  svg {
    width: 18px;
    height: 18px;
    fill: ${({ theme }) => theme.text};
  }

  &:hover {
    background: ${({ theme }) => theme.text}14;
  }
`

S.Card = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  background: ${({ theme }) => theme.card};
  border-radius: 10px;
  padding: 10px 20px;
  font-weight: lighter;

  & .id {
    display: flex;
    min-width: 140px;

    & > span {
      font-size: 12px;
      background: #00000033;
      border-radius: 5px;
      padding: 3px 7px;
    }
  }

  .status{
    display:flex;
    align-items:center;
    padding:2px 6px;
    border-radius:4px;
    span{
      color:inherit !important;
      font-size:0.7em;
    }
  }

  @media(max-width: 800px) {
    flex-wrap: wrap;
    
    & .title {
      margin-left: unset;
      order: 4;
      width: 100%;
    }
    & .card-right {
      order: 3;
    }
  }
  `

S.CardRight = styled.div`
  margin-left: auto;
  display: flex;
  gap: 40px;
  height: fit-content;
  align-items: center;

  & > div {
    position: relative;
    display: flex;
    justify-content: center;

    &:hover span {
      opacity: 1;
    }
    & svg {
      width: 19px;
      height: 19px;
      fill: ${({ theme }) => theme.text};
      cursor: pointer;
    }
    & span {
      z-index: 100;
      opacity: 0;
      position: absolute;
      color: ${({ theme }) => theme.text};
      border-radius: 5px;
      background: ${({ theme }) => theme.card};
      box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
      padding: 3px 8px;
      font-size: 13px;
      top: calc(100% + 10px);
      transition: .3s;
      font-weight: lighter;

      @media(max-width: 1100px) {
        display: none;
      }
    }
  }
  & .price {
    white-space: nowrap;
  }
`

S.Columns = styled(S.Card)`
  padding: 0 20px;
  background: none;
  border-radius: 0;
  font-weight: bolder;
  font-size: 15px;

  & .price {
    margin-left: auto;
    margin-right: 59px;
  }

  @media(max-width: 800px) {
    & .title {
      display: none;
    }
  }
`
